import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { c } from '../theme';
import { Notice, Shell, Wordmark } from '../components/Brand';
import { PillField, SceneHeadline, SceneLead, SceneLink, VioletCTA } from '../components/AuthScene';
import { api, ApiError, type AgreementState, type User } from '../api';
import { useAuth } from '../auth';
import { AGREEMENT_PARTIES, AGREEMENT_PREAMBLE, AGREEMENT_SECTIONS, AGREEMENT_TITLE, AGREEMENT_VERSION } from '../legal/agreement';
import { printPage } from '../native';

/**
 * The pre-release testing agreement, read and signed before anything else.
 *
 * The participant scrolls the whole agreement, types their full name as the
 * signature, confirms they are 18 or over and that they agree. The worker
 * records the name, time, version and a fingerprint of the exact text. After
 * signing, /agreement shows the signed copy, with a way to print it.
 */
export function Agreement({ user, onSigned }: { user: User; onSigned?: () => void }) {
  const navigate = useNavigate();
  const { signOut } = useAuth();
  const [state, setState] = useState<AgreementState | null>(null);
  const [readAll, setReadAll] = useState(false);
  const [fullName, setFullName] = useState('');
  const [over18, setOver18] = useState(false);
  const [agree, setAgree] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const box = useRef<HTMLDivElement>(null);

  useEffect(() => {
    api.agreement().then(setState).catch(() => setState({ version: AGREEMENT_VERSION, signed: null }));
  }, []);

  // Reading to the end unlocks signing.
  const onScroll = () => {
    const el = box.current;
    if (el && el.scrollTop + el.clientHeight >= el.scrollHeight - 24) setReadAll(true);
  };
  useEffect(onScroll, [state]);

  const signed = state?.signed ?? null;
  const ready = readAll && over18 && agree && fullName.trim().split(/\s+/).length >= 2 && !busy;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!ready) return;
    setBusy(true);
    setError(null);
    try {
      const { signed: s } = await api.signAgreement({ version: AGREEMENT_VERSION, fullName: fullName.trim(), over18, agree });
      setState({ version: AGREEMENT_VERSION, signed: s });
      onSigned?.();
      navigate('/search', { replace: true });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not reach the server.');
    } finally {
      setBusy(false);
    }
  };

  const today = new Date().toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' });

  return (
    // The plain app screen, as in the design — no clip behind it.
    <Shell padded={false} style={{ borderRadius: 40 }}>
      <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', flex: 1, padding: '48px var(--gx) 40px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0 8px' }}>
          <Wordmark />
          {signed ? (
            <button type="button" onClick={() => navigate('/search')} style={{ fontSize: 10, letterSpacing: '0.16em', fontWeight: 600, color: 'rgba(31,26,31,0.6)' }}>
              DONE
            </button>
          ) : (
            <button type="button" onClick={() => void signOut()} style={{ fontSize: 10, letterSpacing: '0.16em', fontWeight: 600, color: 'rgba(31,26,31,0.6)' }}>
              SIGN OUT
            </button>
          )}
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: '40px 8px 0' }}>
          <div style={{ fontSize: 10, letterSpacing: '0.2em', fontWeight: 600, color: c.red }}>PRE-RELEASE TESTING</div>
          <SceneHeadline>{signed ? 'Agreement signed' : 'Before you start'}</SceneHeadline>
          <SceneLead>
            {signed
              ? `Signed by ${signed.fullName} on ${new Date(signed.signedAt).toLocaleString('en-AU', { dateStyle: 'long', timeStyle: 'short' })}.`
              : 'ReadyBuy is not yet released. Please read the agreement to the end, then sign to take part.'}
          </SceneLead>
        </div>

        {/* The agreement itself */}
        <div
          ref={box}
          onScroll={onScroll}
          className="rb-agreement"
          style={{
            marginTop: 22,
            maxHeight: signed ? 'none' : 380,
            overflowY: signed ? 'visible' : 'auto',
            padding: '20px 20px 8px',
            borderRadius: 26,
            background: c.tintOpen,
            boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.6)',
            color: c.ink,
            fontSize: 12,
            lineHeight: 1.6,
          }}
        >
          <div style={{ fontSize: 15, fontWeight: 700, letterSpacing: '-0.01em' }}>{AGREEMENT_TITLE}</div>
          <div style={{ fontSize: 9, letterSpacing: '0.14em', fontWeight: 600, color: 'rgba(31,26,31,0.5)', padding: '4px 0 12px' }}>
            {`VERSION ${AGREEMENT_VERSION}`}
          </div>
          <p style={{ margin: '0 0 10px' }}>{AGREEMENT_PARTIES}</p>
          <p style={{ margin: '0 0 14px' }}>{AGREEMENT_PREAMBLE}</p>
          {AGREEMENT_SECTIONS.map((sec, i) => (
            <section key={sec.title} style={{ paddingBottom: 12 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 700, fontSize: 12.5, paddingBottom: 4 }}>
                <span
                  style={{
                    width: 20,
                    height: 20,
                    flex: 'none',
                    borderRadius: '50%',
                    background: c.violet,
                    color: '#FFFFFF',
                    fontSize: 10,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {i + 1}
                </span>
                {sec.title}
              </div>
              {sec.paras.map((p) => (
                <p key={p.slice(0, 40)} style={{ margin: '0 0 6px' }}>
                  {p}
                </p>
              ))}
            </section>
          ))}
          {signed && (
            <div style={{ borderTop: '1px solid rgba(31,26,31,0.15)', padding: '12px 0 8px', fontSize: 11 }}>
              <b>Signed electronically</b> by {signed.fullName} ({user.email}) on{' '}
              {new Date(signed.signedAt).toLocaleString('en-AU', { dateStyle: 'long', timeStyle: 'short' })}, version {signed.version}.
            </div>
          )}
        </div>

        {!signed && (
          <>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: '10px 10px 0',
                fontSize: 9,
                letterSpacing: '0.14em',
                fontWeight: 700,
                color: readAll ? '#1E8A5A' : 'rgba(31,26,31,0.6)',
              }}
            >
              <span
                style={{
                  width: 6,
                  height: 6,
                  borderRadius: '50%',
                  background: readAll ? '#1E8A5A' : c.red,
                  animation: readAll ? 'none' : 'throb 2.4s ease-in-out infinite',
                }}
              />
              {readAll ? 'READ TO THE END' : 'SCROLL TO READ THE WHOLE AGREEMENT'}
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 12, paddingTop: 20, opacity: readAll ? 1 : 0.45, transition: 'opacity .3s' }}>
              <PillField
                dot={c.violet}
                type="text"
                autoComplete="name"
                placeholder="your full name, as your signature"
                aria-label="Full name, as your signature"
                value={fullName}
                disabled={!readAll}
                onChange={(e) => setFullName(e.target.value)}
              />
              <Tick checked={over18} disabled={!readAll} onChange={setOver18}>
                I am 18 or over.
              </Tick>
              <Tick checked={agree} disabled={!readAll} onChange={setAgree}>
                I have read the {AGREEMENT_TITLE} and agree to its terms. I understand typing my name signs it electronically, dated {today}.
              </Tick>
              {error && <Notice tone="error">{error}</Notice>}
              <VioletCTA type="submit" disabled={!ready} style={{ marginTop: 6 }}>
                {busy ? 'signing…' : 'sign and continue'}
              </VioletCTA>
            </div>
          </>
        )}

        {signed && (
          <div style={{ display: 'flex', justifyContent: 'center', gap: 18, paddingTop: 22 }}>
            <SceneLink onClick={() => printPage('ReadyBuy testing agreement')}>PRINT OR SAVE A COPY</SceneLink>
          </div>
        )}
      </form>
    </Shell>
  );
}

function Tick({ checked, disabled, onChange, children }: { checked: boolean; disabled?: boolean; onChange: (v: boolean) => void; children: React.ReactNode }) {
  return (
    <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, fontSize: 12, lineHeight: 1.5, color: c.ink, cursor: disabled ? 'default' : 'pointer', padding: '0 8px' }}>
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
        style={{ width: 18, height: 18, margin: '1px 0 0', flex: 'none', accentColor: c.violet }}
      />
      <span>{children}</span>
    </label>
  );
}

/** The marketing site, where the published terms, privacy policy and disclaimer live. */
export const SITE = 'https://readybuy.au';
/** Must match TERMS_VERSION in the worker. */
const TERMS_VERSION = '2026-10-05';

/**
 * The App Store app's version of the gate: the published terms of use,
 * accepted once, in place of the pre-release testing agreement.
 */
export function AcceptTerms({ onAccepted }: { onAccepted: () => void }) {
  const navigate = useNavigate();
  const { signOut } = useAuth();
  const [over18, setOver18] = useState(false);
  const [agree, setAgree] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!over18 || !agree || busy) return;
    setBusy(true);
    setError(null);
    try {
      await api.acceptTerms({ version: TERMS_VERSION, over18 });
      onAccepted();
      navigate('/search', { replace: true });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not reach the server.');
    } finally {
      setBusy(false);
    }
  };

  const link = (path: string, label: string) => (
    <a href={`${SITE}${path}`} target="_blank" rel="noreferrer" style={{ color: c.violet, fontWeight: 600 }}>{label}</a>
  );

  return (
    <Shell padded={false} style={{ borderRadius: 40 }}>
      <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', flex: 1, padding: '48px var(--gx) 40px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0 8px' }}>
          <Wordmark />
          <button type="button" onClick={() => void signOut()} style={{ fontSize: 10, letterSpacing: '0.16em', fontWeight: 600, color: 'rgba(31,26,31,0.6)' }}>
            SIGN OUT
          </button>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: '40px 8px 0' }}>
          <SceneHeadline>Before you start</SceneHeadline>
          <SceneLead>
            A ReadyBuy report is general information to help you decide what to offer. It is not financial, legal or valuation advice.
          </SceneLead>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 22, padding: '20px', borderRadius: 26, background: c.tintOpen, fontSize: 13, lineHeight: 1.6 }}>
          <div>Please read:</div>
          <div>{link('/terms/', 'Terms of use')}</div>
          <div>{link('/privacy/', 'Privacy policy')}</div>
          <div>{link('/disclaimer/', 'Disclaimer')}</div>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 22 }}>
          <Tick checked={over18} onChange={setOver18}>I am 18 or over.</Tick>
          <Tick checked={agree} onChange={setAgree}>I have read and agree to the Terms of use.</Tick>
        </div>

        {error && <Notice tone="error">{error}</Notice>}
        <div style={{ flex: 1 }} />
        <VioletCTA type="submit" disabled={!over18 || !agree || busy} style={{ marginTop: 24 }}>
          {busy ? 'one moment…' : 'agree and continue'}
        </VioletCTA>
      </form>
    </Shell>
  );
}
